import type { Content, ContentHref, ContentKey, Proposal } from '@generalTypes/apiTypes';
import type { PrivateState } from '@generalTypes/personApiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import {
  selectApiWithPendingChanges,
  selectApiWithPendingChangesOldSlice,
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectAreContentAndProposalsLoaded,
  // selectApiWithPendingChanges,
  selectDocumentRoot,
  selectIsWebpagesFetchedOrNotRequired,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { getPathsToRootWithIsIncludedIn } from '@newStore/externalData/externalDataHelpers';
import { selectHasUserReviewAbility, selectUserHref } from '@newStore/user/userSelectors';
import {
  HoverPositionEnum,
  NodeType,
  type NodeTypeConfigApplied,
} from '@nodeTypeConfig/configTypes';
import { type PayloadAction, createAction } from '@reduxjs/toolkit';
import {
  addAttachment,
  addNodeToParentNodeAction,
  clearSelectionsAction,
  dirtyNodeAction,
  importDocumentInSectionAction,
  moveSelectionsToParentNodeAction,
  updateApiPendingAndWithChangesAction,
  updateDocumentTreeAction,
} from '@store/actions/documentActions';
import { addNotificationAction } from '@store/actions/notificationActions';
import { privateStateApi } from '@store/api/apiConfig';
import {
  DOCUMENT_PUBLISHED,
  DOCUMENT_SAVED,
  UPDATE_DOCUMENT_VIEW_MODEL,
} from '@store/constants/actionTypes';
import {
  getBase64,
  getKeyFromContentHref,
  getNewReadOrder,
  getResourceKey,
} from '@store/helpers/documentHelpers';
import { waitFor } from '@store/helpers/sagaUtils';
import { selectAsideConfig } from '@store/selectors/asideSelectors';
import { call, debounce, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import uuidv4 from 'uuid/v4';
import { settings } from '../../config/settings';
import { createNewPrivateState } from './documentUIHelpers';
import {
  selectAllCollapsibleNodes,
  selectDefaultCollapsedNodes,
  selectDocumentSecurity,
  selectIsDocumentReadOnly,
  selectIsDocumentReadyToShow,
} from './documentUISelectors';
import {
  type AddIsIncludedInDocumentAction,
  type AsideTab,
  type AttachmentGroupModalDataData,
  type DropItemAction,
  type EditNodeAction,
  type GlobalDocumentModalDataData,
  type ItemReferenceModalDataData,
  type Mode,
  type Teaser,
  type UpdateLastReadAction,
  addIsIncludedInDocument,
  closeAside,
  closeAttachmentGroupModal,
  closeTeaserModal,
  dropItem,
  editNode,
  newNodeDropped,
  openAddGlobalDocumentModal,
  openAddItemReferenceModal,
  openAside,
  openAttachmentGroupModal,
  openExtraFieldsModal,
  openTeaserModal,
  selectAreSomeNodesCollapsed,
  setCollapsedNodes,
  setMode,
  setNodeThatShouldBeFocused,
  setPrivateState,
  setSelectedDocument,
  toggleAllCollapsed,
  unCollapseNode,
  updateLastRead,
} from './documentUIState';
import {
  selectAppliedNodeConfig,
  selectContentNodesWithExtraProperties,
  selectDocumentRootType,
  selectTypeNameSingle,
} from './nodeTypeConfigSelectors';

export const createAttachmentsGroup = createAction<{
  importance: Content['importance'];
  createChildType: NodeType.ATTACHMENT | NodeType.GLOBAL_DOCUMENT;
  selectedGlobalDocument: Content | null;
  nodeType: NodeType.ATTACHMENTS_GROUP | NodeType.DOWNLOAD;
}>('documentUI/createAttachmentsGroup');

const fetchPrivateSate = (documentHref, userHref) => {
  return privateStateApi.getAll('/privatestates', {
    owner: userHref,
    context: settings.privateState.component,
    'state.root': documentHref,
  });
};

const putPrivateState = (privateState) => {
  return privateStateApi.put(privateState.$$meta.permalink, privateState);
};

function* savePrivateState() {
  const privateState: PrivateState | null = yield select(
    (state: RootState) => state.documentUI.privateState,
  );
  try {
    yield call(putPrivateState, privateState);
    yield put({ type: UPDATE_DOCUMENT_VIEW_MODEL });
  } catch (error) {
    console.error(error);
    yield put(
      addNotificationAction({
        type: 'WARNING',
        message: `"Alles bekeken" (paarse markering) kan niet juist worden weergegeven`,
      }),
    );
  }
}

function* savePrivateStateUpdateLastRead(action: UpdateLastReadAction) {
  yield call(savePrivateState);
  if (!action.payload || !action.payload.doNotShowLastReadUpdatedNotification) {
    yield put(addNotificationAction({ type: 'SUCCESS', message: 'lastRead.markUpdated' }));
  }
}

function* initialiseDefaultCollapsedState() {
  yield call(waitFor, selectAreContentAndProposalsLoaded);
  yield call(waitFor, selectIsWebpagesFetchedOrNotRequired);
  yield call(waitFor, (state) => selectApiWithPendingChanges(state).relations.length);
  const defaultCollapsedNodes: ReturnType<typeof selectDefaultCollapsedNodes> = yield select(
    selectDefaultCollapsedNodes,
  );
  yield put(setCollapsedNodes({ collapsedNodes: defaultCollapsedNodes }));
}

function* unCollapseNodeSaga(action: PayloadAction<{ nodeHref: ContentHref }>) {
  const relations: RelationsToAndFromMap = yield select((state: RootState) =>
    selectApiWithPendingChangesRelationsToAndFromMap(state),
  );

  const pathsToRoot: ContentHref[][] = getPathsToRootWithIsIncludedIn(
    relations,
    action.payload.nodeHref,
  );

  const ancestorHrefs = [...new Set(pathsToRoot.flat())];

  const collapsedNodes = yield select((state: RootState) => state.documentUI.collapsedNodes);
  const collapsedAncestors = ancestorHrefs.filter((nodeHref) => collapsedNodes[nodeHref]);
  if (collapsedAncestors.length) {
    yield put(
      setCollapsedNodes({
        collapsedNodes: Object.fromEntries(collapsedAncestors.map((nodeHref) => [nodeHref, false])),
      }),
    );
  }
}

function* toggleAllCollapsedSaga() {
  const collapsedNodes: Record<string, boolean> = yield select(
    (state: RootState) => state.documentUI.collapsedNodes,
  );
  const areNodesCollapsed = yield select(selectAreSomeNodesCollapsed);

  if (areNodesCollapsed) {
    yield put(
      setCollapsedNodes({
        collapsedNodes: Object.fromEntries(Object.keys(collapsedNodes).map((key) => [key, false])),
      }),
    );
  } else {
    const collapsibleNodes: ReturnType<typeof selectAllCollapsibleNodes> =
      yield select(selectAllCollapsibleNodes);

    yield put(
      setCollapsedNodes({
        collapsedNodes: collapsibleNodes,
      }),
    );
  }
}

function* initialisePrivateState() {
  try {
    yield call(waitFor, selectAreContentAndProposalsLoaded);
    const root: Content = yield select(selectDocumentRoot);
    if (!root) {
      return;
    }
    const userHref: string = yield select(selectUserHref);
    const privateStates = yield call(fetchPrivateSate, root.$$meta.permalink, userHref);
    if (privateStates.length > 0) {
      yield put(setPrivateState({ privateState: privateStates[0] }));
      return;
    }
    yield put(
      setPrivateState({
        privateState: createNewPrivateState(root.$$meta.permalink, userHref, []),
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een onverwachte fout opgetreden bij het ophalen van de private state.',
      }),
    );
  }
}

function* onDocumentSaved() {
  const rootType: NodeType = yield select(selectDocumentRootType);
  if (rootType !== NodeType.PRONEWSLETTER) {
    yield put(updateLastRead({ doNotShowLastReadUpdatedNotification: true }));
  }
}

function* dropItemSaga(action: DropItemAction) {
  yield put({ type: UPDATE_DOCUMENT_VIEW_MODEL });
  const modalWindowService = getAngularService('ModalWindowService');

  const { droppedItems, parentHref, siblingHref, position } = action.payload;
  console.log(droppedItems, parentHref, position);

  const isNewNode = droppedItems.some((z) => 'newNode' in z && z.newNode === true);

  if (isNewNode && droppedItems.length > 1) {
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Je kan nieuwe nodes slechts 1 per 1 toevoegen.',
      }),
    );
    return;
  }

  const levelParent = yield select((state: RootState) =>
    state.document.viewModel.flatWithHiddens.find((z) => z.key === parentHref.split('/').pop()),
  );

  let positionIndex = 1;

  if (siblingHref !== null) {
    const arrIndex = levelParent.$$children.findIndex(
      (child) => child.key === siblingHref.split('/').pop(),
    );

    if (position === HoverPositionEnum.ABOVE) {
      positionIndex += arrIndex;
    } else {
      positionIndex += arrIndex + 1;
    }
  }

  if (!isNewNode) {
    const relationKeys = droppedItems.map((z) => z.relation.key);
    yield put(moveSelectionsToParentNodeAction(levelParent.key, positionIndex, relationKeys));
    const areWeDroppingASelection: boolean = yield select((state: RootState) =>
      state.document.selections.includes(relationKeys[0]),
    );
    if (areWeDroppingASelection) {
      yield put(clearSelectionsAction());
    }
  } else {
    const [droppedItem] = droppedItems;

    if (droppedItem.node.type === NodeType.WORD_IMPORT) {
      const opts = {
        component: 'documentImportModal',
        dataForModal: { parentDocument: levelParent, positionIndex },
      };

      const importResult = yield modalWindowService.open(opts);

      console.log('Imported nodes:', importResult.resources);

      // mutating some stuff here, as it was
      importResult.attachmentsToUpload.forEach(async (upload) => {
        const base64 = await getBase64(upload.file);
        upload.$$base64 = base64;
      });
      yield put(importDocumentInSectionAction(importResult, levelParent.key));
    } else if ([NodeType.ATTACHMENTS_GROUP, NodeType.DOWNLOAD].includes(droppedItem.node.type)) {
      const data: AttachmentGroupModalDataData = {
        parentHref,
        positionIndex,
        nodeType: droppedItem.node.type as NodeType.ATTACHMENTS_GROUP | NodeType.DOWNLOAD,
      };

      yield put(openAttachmentGroupModal(data));
    } else if (droppedItem.node.type === NodeType.GLOBAL_DOCUMENT) {
      const data: GlobalDocumentModalDataData = {
        parentHref,
        positionIndex,
        nodeType: droppedItem.node.type,
      };

      yield put(openAddGlobalDocumentModal(data));
    } else if (
      droppedItem.node.type === NodeType.PERSON_REFERENCE ||
      droppedItem.node.type === NodeType.PRO_NEWSLETTER_EVENT
    ) {
      const data: ItemReferenceModalDataData = {
        parentHref,
        positionIndex,
        nodeType: droppedItem.node.type,
      };
      yield put(openAddItemReferenceModal(data));
    } else if (droppedItem.node.type === NodeType.TEASER) {
      yield put(openTeaserModal(parentHref));

      const action = yield take(closeTeaserModal.type);
      const teaser: Teaser = action.payload;

      if (teaser) {
        yield put(
          addIsIncludedInDocument({
            parentHref: parentHref as ContentHref,
            includedInDocument: teaser.$$expanded.$$meta.permalink,
            position: positionIndex,
          }),
        );
      }
    } else {
      const newKey = uuidv4();
      yield put(
        addNodeToParentNodeAction(
          levelParent.key,
          positionIndex,
          droppedItem.node.type,
          undefined,
          newKey,
        ),
      );

      if ('file' in droppedItem && droppedItem.file) {
        const { file } = droppedItem;
        if (/\.(jpe?g|png)$/i.test(file.name)) {
          const base64 = yield getBase64(file);
          const newAttachment = {
            key: uuidv4(),
            newKey: uuidv4(),
            type: 'ILLUSTRATION',
            name: file.name,
            size: file.size,
            $$base64: base64,
            isNew: true,
          };

          yield put(addAttachment(newKey, newAttachment, file));
        }
      }
    }
  }
}

function* calculateDefaultMode() {
  try {
    yield call(waitFor, (state: RootState) => state.documentApi.isContentAndRelationsFetched);
    yield call(waitFor, (state: RootState) => selectIsWebpagesFetchedOrNotRequired(state));
    yield call(waitFor, (state: RootState) => state.newUser.areUserRolesFetched);

    const documentSecurity: ReturnType<typeof selectDocumentSecurity> = yield select(
      (state: RootState) => selectDocumentSecurity(state),
    );

    if (documentSecurity.canSuggest) {
      yield call(waitFor, (state: RootState) => state.documentApi.isProposalsFetched);
      const proposals: Proposal[] = yield select((state: RootState) =>
        Object.values(state.documentApi.proposals),
      );

      if (documentSecurity.canReview) {
        const hasSubimttedProposal = proposals.some((p) => p.status === 'SUBMITTED_FOR_REVIEW');
        yield put(setMode(hasSubimttedProposal ? 'REVIEW' : 'EDIT'));
      } else {
        yield put(setMode('SUGGEST'));
      }
    } else {
      yield put(setMode('EDIT'));
    }
  } catch (e) {
    console.error('Something went wrong calculating the default mode', e);
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een onverwachte fout opgetreden bij het bereken van de mode.',
      }),
    );
    yield put(setMode('EDIT'));
  }
}

function* alertReadOnlyModeSaga() {
  const mode: Mode | null = yield select((state: RootState) => state.documentUI.mode);
  const isDocumentReadonly: ReturnType<typeof selectIsDocumentReadOnly> = yield select(
    (state: RootState) => selectIsDocumentReadOnly(state),
  );

  if (mode && isDocumentReadonly) {
    // eslint-disable-next-line no-nested-ternary
    const modeString = mode === 'EDIT' ? 'edit' : mode === 'REVIEW' ? 'review' : 'suggest';
    yield put(
      addNotificationAction({
        type: 'WARNING',
        message: `Je kan dit document niet bewerken in ${modeString} mode.`,
      }),
    );
  }
}

function* onNewNodeDropped(action: PayloadAction<{ newHref: ContentHref }>) {
  const { newHref } = action.payload;
  const nodeTypeConfig: ReturnType<typeof selectAppliedNodeConfig> = yield select(
    (state: RootState) => selectAppliedNodeConfig(state, newHref),
  );
  if ('onNewNodeDropped' in nodeTypeConfig) {
    if (nodeTypeConfig.onNewNodeDropped.openAside) {
      yield put(openAside({ href: newHref }));
    } else if (nodeTypeConfig.onNewNodeDropped.focusOnField) {
      yield put(
        setNodeThatShouldBeFocused({
          field: nodeTypeConfig.onNewNodeDropped.focusOnField,
          href: newHref,
        }),
      );
    }
  }
}

/**
 * make sure that the state does not keep saying it should focus on this node indefinetely
 */
function* resetFocussedNode() {
  // give InlineEditor component time to react on the state.nodeThatShouldBeFocused before setting it to null again
  const value = yield select((state: RootState) => state.documentUI.nodeThatShouldBeFocused);
  if (value !== null) {
    yield delay(500);
    yield put(setNodeThatShouldBeFocused(null));
  }
}

function* createAttachmentsGroupSaga(
  action: PayloadAction<{
    importance: Content['importance'];
    createChildType: NodeType.ATTACHMENT | NodeType.GLOBAL_DOCUMENT;
    selectedGlobalDocument: Content | null;
    nodeType: NodeType.ATTACHMENTS_GROUP | NodeType.DOWNLOAD;
  }>,
) {
  const { importance, createChildType, selectedGlobalDocument, nodeType } = action.payload;
  const attachmentGroupModalData: AttachmentGroupModalDataData = yield select(
    (state: RootState) => state.documentUI.attachmentGroupModalData.data,
  );
  const { parentHref, positionIndex } = attachmentGroupModalData;
  const groupKey = uuidv4();
  const groupHref = `/content/${groupKey}` as ContentHref;
  yield put(
    addNodeToParentNodeAction(
      getResourceKey(parentHref),
      positionIndex,
      nodeType,
      { importance },
      groupKey,
    ),
  );
  if (createChildType === NodeType.GLOBAL_DOCUMENT && selectedGlobalDocument) {
    yield put(
      addIsIncludedInDocument({
        parentHref: groupHref,
        includedInDocument: selectedGlobalDocument.$$meta.permalink,
        position: 0,
      }),
    );
  } else {
    yield put(addNodeToParentNodeAction(groupKey, 0, createChildType));
  }
  yield put(closeAttachmentGroupModal());
}

function* closeAsideSaga() {
  const currentDocumentHref = yield select((state: RootState) => state.documentUI.currentDocument);
  getAngularService('$state').go('edit', { key: getResourceKey(currentDocumentHref) });
}

// eslint-disable-next-line require-yield
// biome-ignore lint/correctness/useYield: <explanation>
function* openAsideSaga(action: PayloadAction<{ href: ContentHref; tab?: AsideTab }>) {
  const editKey = getResourceKey(action.payload.href) as ContentKey;
  getAngularService('$state').go('edit.aside', { editKey });
}

function* editNodeSaga(action: EditNodeAction) {
  const { href, config } = action.payload;
  const nodeTypeConfig: NodeTypeConfigApplied = yield select((state: RootState) =>
    selectAppliedNodeConfig(state, href),
  );
  if ('onEditIncludedNode' in nodeTypeConfig) {
    nodeTypeConfig.onEditIncludedNode(href);
  } else {
    let asideTab: AsideTab = yield select((state: RootState) => state.documentUI.asideTab);

    // find out which tab in the aside to land at.
    if (config) {
      for (const tab of ['metadata', 'audiences', 'web'] as const) {
        const tabConfig = yield select((state: RootState) => selectAsideConfig(state, href, tab));
        if (
          tabConfig?.some((z) => z.component === config.component && z.property === config.property)
        ) {
          asideTab = tab;
          break;
        }
      }
    }

    yield put(openAside({ href, asideTab }));
  }
}

function* addIsIncludedInDocumentSaga(action: AddIsIncludedInDocumentAction) {
  const { parentHref, includedInDocument, position } = action.payload;

  const existingRelation = yield select((state: RootState) =>
    selectApiWithPendingChangesRelationsToAndFromMap(state).to[parentHref].find(
      (z) => z.from.href === includedInDocument,
    ),
  );

  if (existingRelation) {
    const includedDocumentName: string = yield select((state: RootState) =>
      selectTypeNameSingle(state, includedInDocument),
    );
    const parentName: string = yield select((state: RootState) =>
      selectTypeNameSingle(state, parentHref),
    );
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: `Je kan een ${includedDocumentName.toLowerCase()} niet meerdere keren toevoegen onder eenzelfde ${parentName.toLowerCase()}.`,
      }),
    );
    return;
  }

  const newApiWithPendingChanges: ReturnType<typeof selectApiWithPendingChangesOldSlice> =
    yield select((state: RootState) => selectApiWithPendingChangesOldSlice(state));
  const parent = newApiWithPendingChanges.content.get(parentHref) as any;

  const readOrderObject = getNewReadOrder(position, parent.$$children, 1);
  const key = uuidv4();
  const permalink = `/content/relations/${key}`;
  const newRelation = {
    key,
    $$meta: {
      permalink,
      type: 'relation',
    },
    relationtype: 'IS_INCLUDED_IN',
    readorder: readOrderObject.previousReadOrder + readOrderObject.incrementGap,
    from: {
      href: includedInDocument,
    },
    to: {
      href: parentHref,
    },
  };

  const resources = [
    {
      href: permalink,
      parentHref,
      relatedTo: { href: includedInDocument },
      body: newRelation,
    },
  ];

  yield put({
    type: 'ADD_PENDING_ACTION',
    payload: {
      type: 'CREATE',
      resources,
    },
  });

  /**
   * these actions were found at ADD_GLOBAL_DOCUMENT_RELATION and some at ADD_TEASER_SUCCESS so I am adding them here just to be sure.
   */
  yield put(dirtyNodeAction(getKeyFromContentHref(parentHref), false));
  yield put(updateApiPendingAndWithChangesAction());
  yield put(updateDocumentTreeAction());
}

let previousExtraPropertiesString = '';
function* checkForExtraFieldSaga() {
  try {
    const canReview: ReturnType<typeof selectHasUserReviewAbility> = yield select((state: RootState) =>
      selectHasUserReviewAbility(state),
    );

    const isDocLoaded: ReturnType<typeof selectIsDocumentReadyToShow> = yield select(
      selectIsDocumentReadyToShow,
    );

    if (!canReview || !isDocLoaded) {
      return;
    }

    const extraProperties: ReturnType<typeof selectContentNodesWithExtraProperties> = yield select(
      (state: RootState) => selectContentNodesWithExtraProperties(state),
    );

    const extraPropertiesString = extraProperties
      .map((exp) => `${exp.item.$$meta.permalink}: ${exp.extraDefinedProperties.join(', ')}`)
      .join('; ');

    if (extraPropertiesString !== previousExtraPropertiesString) {
      previousExtraPropertiesString = extraPropertiesString;
      if (extraProperties.length > 0) {
        yield put(openExtraFieldsModal());
      }
    }
  } catch (error) {
    console.error('Error in checkForExtraFieldSaga:', error);
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een onverwachte fout opgetreden bij het controleren van extra velden.',
      }),
    );
  }
}

export function* documentUISaga() {
  yield takeEvery(setSelectedDocument.match, initialisePrivateState);
  yield takeEvery(setSelectedDocument.match, initialiseDefaultCollapsedState);
  // yield takeEvery(setSelectedDocument.match, checkAsideEditSaga);

  yield takeEvery([setSelectedDocument, DOCUMENT_PUBLISHED], calculateDefaultMode);

  yield takeEvery(addIsIncludedInDocument.match, addIsIncludedInDocumentSaga);

  yield takeEvery(toggleAllCollapsed.match, toggleAllCollapsedSaga);
  yield takeLatest(updateLastRead.match, savePrivateStateUpdateLastRead);
  yield takeLatest(DOCUMENT_SAVED, onDocumentSaved);
  yield takeLatest(unCollapseNode, unCollapseNodeSaga);
  yield takeEvery(dropItem.match, dropItemSaga);
  yield takeLatest(newNodeDropped, onNewNodeDropped);
  yield takeLatest(setNodeThatShouldBeFocused, resetFocussedNode);

  yield takeLatest(createAttachmentsGroup, createAttachmentsGroupSaga);

  yield takeLatest(setMode, alertReadOnlyModeSaga);

  yield takeLatest(closeAside, closeAsideSaga);
  yield takeLatest(openAside, openAsideSaga);
  yield takeLatest(editNode, editNodeSaga);

  yield debounce(2000, '*', checkForExtraFieldSaga);
}
